<template>
  <div class="rounded-xl border border-primary/30 bg-primary/5 p-3 text-sm" data-test="letter-questions">
    <div class="flex items-center justify-between gap-2">
      <p class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
        <i class="fas fa-wand-magic-sparkles text-[10px]"></i>
        Before we write your letter
      </p>
      <span v-if="total > 1" class="text-[11px] text-muted-foreground" data-test="letter-questions-progress">
        {{ position }} of {{ total }}
      </span>
    </div>

    <template v-if="current">
      <p class="mt-1.5 text-foreground leading-relaxed font-medium" data-test="letter-question">{{ current.question }}</p>
      <p class="mt-0.5 text-[11px] text-muted-foreground">The job asks for this and your profile doesn't say yet.</p>

      <div v-if="current.options && current.options.length" class="mt-2 flex flex-wrap gap-1.5" data-test="letter-question-options">
        <button
          v-for="option in current.options"
          :key="option.label"
          type="button"
          :disabled="saving"
          class="px-3 py-1.5 rounded-full border border-border bg-background text-xs font-medium text-foreground hover:border-primary hover:text-primary disabled:opacity-40 transition-colors"
          @click="answer(option.value)"
        >
          {{ option.label }}
        </button>
        <button
          v-if="!typing"
          type="button"
          :disabled="saving"
          class="px-3 py-1.5 rounded-full border border-dashed border-border text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-40"
          data-test="letter-question-type-own"
          @click="typing = true"
        >
          <i class="fas fa-pen text-[10px] mr-1"></i>Type my own
        </button>
      </div>

      <form
        v-if="typing || !current.options || !current.options.length"
        class="mt-2 flex gap-2"
        data-test="letter-question-form"
        @submit.prevent="answer(draft)"
      >
        <input
          ref="input"
          v-model="draft"
          :type="current.input === 'url' ? 'url' : 'text'"
          :placeholder="current.input === 'url' ? 'https://' : 'Type your answer'"
          maxlength="500"
          :disabled="saving"
          class="min-w-0 flex-1 h-9 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-60"
        />
        <button
          type="submit"
          :disabled="saving || !draft.trim()"
          class="shrink-0 h-9 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-40"
        >
          Save
        </button>
      </form>

      <div class="mt-2.5 flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          :disabled="saving"
          class="text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-40"
          data-test="letter-question-skip"
          @click="skip"
        >
          Skip this question
        </button>
        <button
          type="button"
          :disabled="saving"
          class="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary hover:underline disabled:opacity-40"
          data-test="letter-questions-write-now"
          @click="finish"
        >
          <i v-if="saving" class="fas fa-spinner fa-spin text-[10px]"></i>
          Skip, just write it
        </button>
      </div>
      <p v-if="error" class="mt-1 text-[11px] text-red-600 dark:text-red-400">{{ error }}</p>
    </template>
  </div>
</template>

<script>
import { computed, nextTick, ref, watch } from "vue";
import jobPreflight from "@/services/jobPreflight";

/**
 * The few things a job post asks for that the applicant's profile doesn't
 * have yet, asked one at a time before the letter is written. Each answer is
 * saved to the profile, so it's never asked again and every later letter has
 * it; a skipped question isn't asked again either. Emits "done" when every
 * question is answered or skipped, or the user chooses to write right away.
 */
export default {
  name: "LetterQuestions",
  props: {
    jobId: { type: [Number, String], required: true },
    // Gaps from POST /cover-letters/preflight/: {id, question, input, options, allow_custom}.
    questions: { type: Array, required: true },
  },
  emits: ["done"],
  setup(props, { emit }) {
    const queue = ref([...props.questions]);
    const total = computed(() => props.questions.length);
    const saving = ref(false);
    const error = ref("");
    const typing = ref(false);
    const draft = ref("");
    const input = ref(null);

    const current = computed(() => queue.value[0] || null);
    const position = computed(() => total.value - queue.value.length + 1);

    watch(typing, async (on) => {
      if (!on) return;
      await nextTick();
      if (input.value) input.value.focus();
    });

    const finish = () => emit("done");

    const next = (updatedGaps) => {
      typing.value = false;
      draft.value = "";
      // Drop the one just handled, and any the answer happened to cover.
      const stillOpen = new Set((updatedGaps || []).map((g) => g.id));
      queue.value = queue.value.slice(1).filter((g) => !updatedGaps || stillOpen.has(g.id));
      if (!queue.value.length) finish();
    };

    const send = async (payload) => {
      if (!current.value || saving.value) return;
      saving.value = true;
      error.value = "";
      try {
        const result = await jobPreflight.answer(props.jobId, current.value.id, payload);
        next(result?.gaps);
      } catch (e) {
        error.value = e?.response?.data?.error || "Couldn't save that. Try again, or skip it.";
      } finally {
        saving.value = false;
      }
    };

    const answer = (value) => {
      const text = String(value || "").trim();
      if (text) send({ answer: text });
    };
    const skip = () => send({ skipped: true });

    return { current, position, total, saving, error, typing, draft, input, answer, skip, finish };
  },
};
</script>
